import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { checkSupabaseConnection, isSupabaseConfigured } from '../lib/supabaseClient';
import Modal from './Modal';

const SupabaseStatus = () => {
  const [statusInfo, setStatusInfo] = useState({
    loading: true,
    connected: false,
    status: 'checking',
    message: 'Checking connection...',
    latency: null,
    tableChecks: {}
  });
  const [isOpen, setIsOpen] = useState(false);

  const runCheck = async () => {
    setStatusInfo(prev => ({ ...prev, loading: true }));
    try {
      const res = await checkSupabaseConnection();
      setStatusInfo({
        loading: false,
        connected: res.connected,
        status: res.status,
        message: res.message,
        latency: res.latency,
        tableChecks: res.tableChecks || {}
      });
    } catch (err) {
      setStatusInfo({
        loading: false,
        connected: false,
        status: 'error',
        message: err.message || 'Connection failed',
        latency: null,
        tableChecks: {}
      });
    }
  };

  useEffect(() => {
    runCheck();
  }, []);

  const getBadgeColor = () => {
    if (statusInfo.loading) return '#94a3b8';
    if (statusInfo.status === 'healthy') return '#10b981';
    if (statusInfo.status === 'schema_missing' || statusInfo.status === 'unconfigured') return '#f59e0b';
    return '#ef4444';
  };

  const getBadgeText = () => {
    if (statusInfo.loading) return 'Checking DB...';
    if (statusInfo.status === 'healthy') return 'Supabase Connected';
    if (statusInfo.status === 'schema_missing') return 'DB Schema Needed';
    if (statusInfo.status === 'unconfigured') return 'Supabase Offline';
    return 'DB Connection Error';
  };

  return (
    <>
      <div 
        onClick={() => setIsOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          margin: '8px 0',
          borderRadius: '8px',
          cursor: 'pointer',
          background: 'rgba(255, 255, 255, 0.05)',
          border: `1px solid ${getBadgeColor()}40`,
          fontSize: '0.8rem',
          transition: 'all 0.2s ease',
          userSelect: 'none'
        }}
        title="Click to check Supabase database connectivity details"
      >
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: getBadgeColor(),
          boxShadow: `0 0 6px ${getBadgeColor()}`
        }} />
        <Database size={15} style={{ color: getBadgeColor() }} />
        <span style={{ color: '#e2e8f0', fontWeight: 500 }}>{getBadgeText()}</span>
      </div>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Supabase Database Connectivity">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', color: '#f8fafc' }}>
          
          {/* Header Status Card */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px',
            borderRadius: '10px',
            background: statusInfo.status === 'healthy' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
            border: `1px solid ${getBadgeColor()}40`
          }}>
            {statusInfo.status === 'healthy' ? (
              <CheckCircle size={28} color="#10b981" />
            ) : statusInfo.status === 'unconfigured' || statusInfo.status === 'schema_missing' ? (
              <AlertTriangle size={28} color="#f59e0b" />
            ) : (
              <XCircle size={28} color="#ef4444" />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                {statusInfo.status === 'healthy' ? 'Connected to Supabase' : 'Connection Status: ' + statusInfo.status}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '2px' }}>
                {statusInfo.message}
              </div>
            </div>
          </div>

          {/* Latency / Env Details */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
            background: 'rgba(0, 0, 0, 0.2)',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '0.85rem'
          }}>
            <div>
              <span style={{ color: '#94a3b8' }}>Configuration: </span>
              <strong style={{ color: isSupabaseConfigured() ? '#10b981' : '#f59e0b' }}>
                {isSupabaseConfigured() ? 'Active (.env)' : 'Missing (.env)'}
              </strong>
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>Latency: </span>
              <strong>{statusInfo.latency ? `${statusInfo.latency} ms` : 'N/A'}</strong>
            </div>
          </div>

          {/* Table Verification Checks */}
          {statusInfo.connected && (
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', color: '#cbd5e1' }}>
                Table Health Checks:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {['tenants', 'expenses', 'invoices', 'inventory'].map(table => (
                  <div key={table} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    fontSize: '0.85rem'
                  }}>
                    <span>{table}</span>
                    {statusInfo.tableChecks[table] ? (
                      <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CheckCircle size={14} /> Ready
                      </span>
                    ) : (
                      <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <XCircle size={14} /> Missing
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Setup Instructions if unconfigured */}
          {!isSupabaseConfigured() && (
            <div style={{
              background: 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '8px',
              padding: '12px',
              fontSize: '0.85rem',
              lineHeight: 1.5
            }}>
              <strong style={{ color: '#60a5fa' }}>How to connect:</strong>
              <ol style={{ paddingLeft: '1.2rem', marginTop: '6px', marginBlockEnd: 0 }}>
                <li>Open <code>.env</code> in the project root.</li>
                <li>Set <code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code> from your Supabase Dashboard.</li>
                <li>Run <code>supabase_schema.sql</code> in the Supabase SQL Editor.</li>
                <li>Restart your React app (<code>npm start</code>).</li>
              </ol>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              onClick={runCheck}
              disabled={statusInfo.loading}
              className="btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <RefreshCw size={14} className={statusInfo.loading ? 'animate-spin' : ''} />
              {statusInfo.loading ? 'Testing...' : 'Test Connection'}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="btn-primary"
            >
              Close
            </button>
          </div>

        </div>
      </Modal>
    </>
  );
};

export default SupabaseStatus;
